import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  Divider,
  Grid,
  Header,
  Segment,
  Table,
  Button,
  Modal,
  Icon,
  Accordion,
  Input,
  Dropdown
} from 'semantic-ui-react';
import { copyToClipboard, commandToText } from '../../utils';
import { servicesAPI } from '../../_services/services_api';
import { useCurrentUser, usePhalanxByReference } from '../../_services';
import { setToast } from '../../store/ToastManagement';
import { dbUTCDatetimeToLocal } from '../../utils';
import { ChargingSessionStates } from './ChargingSessionStates';
import Moment from 'moment-timezone';
import { extendMoment } from 'moment-range';
import './Map.css';

const moment = extendMoment(Moment);

const axonIdOptions = [
  { text: 'AXON 01', value: 1 },
  { text: 'AXON 02', value: 2 },
  { text: 'AXON 03', value: 3 },
  { text: 'AXON 04', value: 4 },
  { text: 'AXON 05', value: 5 },
  { text: 'AXON 06', value: 6 },
  { text: 'AXON 07', value: 7 },
  { text: 'AXON 08', value: 8 },
  { text: 'AXON 09', value: 9 },
  { text: 'AXON 10', value: 10 },
  { text: 'AXON 11', value: 11 },
  { text: 'AXON 12', value: 12 },
  { text: 'AXON 13', value: 13 },
  { text: 'AXON 14', value: 14 },
  { text: 'AXON 15', value: 15 },
  { text: 'All AXONs', value: 255 }
];

const satecConfigOptions = [
  { text: 'Normal', value: 1 },
  { text: 'Swapped', value: 2 },
  { text: 'Custom', value: 3 }
];

export const DisplayHUBInterface = ({ history, location }) => {
  const hubRef = location.pathname.split('/')[3];
  const phalanxRef = hubRef + '_cs_00';
  const phalanx = usePhalanxByReference(phalanxRef);
  const [hydra, setHydra] = useState(null);
  const { currentUser, currentUserCan } = useCurrentUser();
  const [tableData, setTableData] = useState([]);
  const [axonId, setAxonId] = useState(1);
  const [current, setCurrent] = useState(32);
  const [satecConfig, setSatecConfig] = useState(1);
  const [satecPauseMinutes, setSatecPauseMinutes] = useState(0);
  const [pauseModbusServerMinutes, setPauseModbusServerMinutes] = useState(0);
  const [messageByteStringValid, setMessageByteStringValid] = useState(false);
  const [messageByteString, setMessageByteString] = useState('');
  const [messageLength, setMessageLength] = useState(0);
  const [cmd, setCmd] = useState(0);
  const [command, setCommand] = useState('');
  const [param1, setParam1] = useState('');
  const [param2, setParam2] = useState('');
  const [rerouteCommand, setRerouteCommand] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [statusHide, setStatusHide] = useState(false);
  const [lastUpdated, setLastUpdated] = useState('');

  const dispatch = useDispatch();

  const getHydra = () => {
    servicesAPI.InventoryServices('getHydra', phalanx.hydra_tag_id, false).then(
      hydra => {
        setHydra(hydra);
      },
      error => {}
    );
  };

  async function getData(phalanx_tag_id, meter_tag_id) {
    try {
      // Check for outdated timestamps
      const dtLastHeartbeat = moment.utc(
        phalanx.last_heartbeat_time,
        'YYYY-MM-DDTHH:mm:ss,Z'
      );
      const dtNow = moment.utc().local();
      const elapsedMilliseconds = moment.duration(dtNow.diff(dtLastHeartbeat))
        ._milliseconds;

      // Flag comms lost
      phalanx.no_heartbeat = elapsedMilliseconds >= 900 * 1000;

      let tableData = [
        {
          property: 'Reference',
          value: phalanx.reference
        },
        {
          property: 'Meter Tag ID',
          value: phalanx ? phalanx.meter_tag_id : ''
        },
        {
          property: 'AXON Firmware Version',
          value: phalanx ? phalanx.axon_fw_version : ''
        },
        {
          property: 'Last Heartbeat',
          value: phalanx?.no_heartbeat ? (
            <p style={{ color: 'red' }}>
              {dbUTCDatetimeToLocal(phalanx?.last_heartbeat_time)}
            </p>
          ) : (
            dbUTCDatetimeToLocal(phalanx?.last_heartbeat_time)
          )
        }
      ];

      setTableData(tableData);
    } catch (error) {}
  }

  useEffect(() => {
    if (phalanx) getHydra();
  }, [phalanx]);

  useEffect(() => {
    if (hydra && phalanx) getData(phalanx.tag_id, phalanx.meter_tag_id);
  }, [hydra, phalanx]);

  useEffect(() => {
    const valid = /^[0-9a-fA-F][0-9a-fA-F]([0-9a-fA-F][0-9a-fA-F])*$/.test(
      messageByteString
    );
    const numBytes = messageByteString.length / 2;

    setMessageByteStringValid(valid && messageLength >= numBytes - 1);
  }, [messageLength, messageByteString]);

  const confirmSendCommand = (
    command,
    param1,
    param2,
    confirmation,
    reroute
  ) => {
    setIsModalOpen(true);
    setCommand(command);
    setParam1(param1);
    setParam2(param2);
    setConfirmation(confirmation);
    setRerouteCommand(reroute);
  };

  const sendCommand = () => {
    setIsModalOpen(false);

    if (command === 'COPY_DB_TO_REDIS') {
      servicesAPI
        .InventoryServices('getPhalanxByReference', phalanx.reference, true)
        .then(response => {
          dispatch(
            setToast({
              title: 'Cache refreshed!',
              body: ''
            })
          );
        });
    } else if (command.startsWith('HUB_') || command === 'SEND_MESSAGE') {
      const hubInterfaceRef = hydra.reference + '_cs_00';

      let cmd = command;
      if (param1 !== '') cmd += '_' + param1;
      if (param2 !== '') cmd += '_' + param2;
      console.log(`${cmd}: ${command},${param1},${param2}`);

      const timestamp = moment.utc().format('YYYY-MM-DDTHH:mm:ss') + ',Z';

      const values = {
        csms_command: cmd + ',' + timestamp
      };

      servicesAPI
        .InventoryServices('updatePhalanxByReference', values, hubInterfaceRef)
        .then(
          response => {
            dispatch(
              setToast({
                title: `Command sent to HUB!`,
                body: 'Check Google Cloud logs to confirm'
              })
            );
            servicesAPI.AdminServices(
              'auditUserActivity',
              {
                entity_type: 'HUB_INTERFACE',
                entity_id: hydra.reference + '_cs_00',
                activity: 'SupportOperation',
                notes: `${hydra.description}: ${commandToText(cmd)}`,
                email_subject: '',
                email_body: ''
              },
              currentUser.id
            );
          },
          error => {}
        );
      // Route message via an available charge station
    } else if (rerouteCommand) {
      servicesAPI.InventoryServices('getHydraPhalanxes', hydra.tag_id).then(
        phalanxes => {
          let availablePhalanxes = [];
          phalanxes.forEach(p => {
            if (!p.reference.endsWith('cs_00') && p.status === 'Available')
              availablePhalanxes.push(p);
          });

          if (availablePhalanxes.length) {
            const availablePhalanx =
              availablePhalanxes[
                Math.floor(Math.random() * availablePhalanxes.length)
              ];

            const timestamp = moment.utc().format('YYYY-MM-DDTHH:mm:ss') + ',Z';

            const values = {
              csms_command: command + ',' + timestamp
            };
            servicesAPI
              .InventoryServices(
                'updatePhalanxByReference',
                values,
                availablePhalanx.reference
              )
              .then(
                response => {
                  const csId = availablePhalanx.reference.slice(-2);
                  dispatch(
                    setToast({
                      title: `Command sent to HUB via CS${csId}!`,
                      body: 'Check Google Cloud logs to confirm'
                    })
                  );
                  servicesAPI.AdminServices(
                    'auditUserActivity',
                    {
                      entity_type: 'HYDRA',
                      entity_id: hydra.reference,
                      activity: 'SupportOperation',
                      notes: `${hydra.description}: ${commandToText(command)}`,
                      email_subject: '',
                      email_body: ''
                    },
                    currentUser.id
                  );
                },
                error => {}
              );
          }
        },
        error => {}
      );
    }
  };

  if (!phalanx?.tag_id) return 'Loading...';

  return (
    <Segment>
      <Grid stretched>
        <Grid.Row>
          <Grid.Column>
            <Header
              as="h2"
              onClick={() => history.push(`/admin/hubs/${hydra.reference}`)}
            >
              AXON HUB Interface: {hydra?.description}
            </Header>
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <Divider hidden />
      <Divider hidden />
      <Grid>
        <Accordion>
          <Accordion.Title
            active={!statusHide}
            index={0}
            onClick={() => {
              setStatusHide(!statusHide);
            }}
          >
            <Header as="h2">
              <Icon name="dropdown" />
              Status
            </Header>
          </Accordion.Title>
          <Accordion.Content active={!statusHide}>
            <Table
              basic="very"
              selectable
              stackable
              renderBodyRow={({ property, value }, idx) => ({
                key: idx,
                cells: [
                  {
                    key: 'property',
                    onClick: () => {
                      copyToClipboard(property);

                      const bits = phalanx.reference.split('_cs');
                      const hubRef = bits[0];
                      const csId = 'cs' + bits[1];

                      history.push(`/admin/hubs/${hubRef}/${csId}`);
                    },
                    content: property
                  },
                  {
                    key: 'value',
                    onClick: () => {
                      copyToClipboard(value);

                      const bits = phalanx.reference.split('_cs');
                      const hubRef = bits[0];
                      const csId = 'cs' + bits[1];

                      if (property !== 'reference')
                        history.push(`/admin/hubs/${hubRef}/${csId}/sessions`);
                    },
                    content:
                      value === 'FAULTED' ? (
                        <p style={{ color: 'red' }}>{value}</p>
                      ) : (
                        value
                      )
                  }
                ]
              })}
              tableData={tableData}
            />
          </Accordion.Content>
        </Accordion>
      </Grid>
      <Divider hidden />
      <Divider hidden />
      {phalanx ? (
        <ChargingSessionStates
          phalanxTagId={phalanx.tag_id}
          phalanxRef={phalanx.reference}
          lance={null}
          isAXON={true}
          setLanceDetected={false}
          lastUpdated={lastUpdated}
          setLastUpdated={setLastUpdated}
        />
      ) : (
        ''
      )}
      <Divider hidden />
      <Divider hidden />
      {currentUserCan('DoAllTheThings') && (
        <>
          <Header as="h2">Portal</Header>
          <Button
            onClick={() => {
              confirmSendCommand(
                'REFRESH_CACHE',
                '',
                '',
                'Are you sure you want to refresh the dashboard for this HUB interface?',
                false
              );
            }}
            color="olive"
          >
            Refresh Dashboard Status
          </Button>
          <Button
            onClick={() => {
              confirmSendCommand(
                'COPY_DB_TO_REDIS',
                '',
                '',
                'Are you sure you want to refresh the cache to match inventory.phalanx?',
                false
              );
            }}
            color="olive"
          >
            Refresh DB cache
          </Button>
          <br />
          <br />
        </>
      )}
      <Header as="h2">HUB Interface</Header>
      <Button
        onClick={() => {
          confirmSendCommand(
            'HUB_REBOOT',
            '',
            '',
            'Are you sure you want to reboot the HUB interface?',
            false
          );
        }}
        color="olive"
      >
        Reboot HUB interface
      </Button>
      <br />
      <br />
      <Button
        onClick={() => {
          confirmSendCommand(
            'HUB_DEBUG_OFF',
            '',
            '',
            'Are you sure you want to disable additional debugging?',
            false
          );
        }}
        color="olive"
      >
        Disable debugging
      </Button>
      <Button
        onClick={() => {
          confirmSendCommand(
            'HUB_DEBUG_ON',
            '',
            '',
            'Are you sure you want to enable additional debugging?',
            false
          );
        }}
        color="olive"
      >
        Enable debugging
      </Button>
      <br />
      <br />
      <Dropdown
        selection
        search
        options={axonIdOptions}
        onChange={(e, inputProps) => {
          setAxonId(inputProps.value);
        }}
        value={axonId}
      />
      <Button
        onClick={() => {
          confirmSendCommand(
            'HUB_CONFIGURE_AXON',
            axonId,
            '',
            'Are you sure you want to force configuration of channel for AXON(s)?',
            false
          );
        }}
        color="olive"
      >
        Force configuration of channel
      </Button>
      <br />
      <br />
      <Dropdown
        selection
        search
        options={axonIdOptions}
        onChange={(e, inputProps) => {
          setAxonId(inputProps.value);
        }}
        value={axonId}
      />
      <Input
        type="number"
        label={'Current (0-32A)'}
        value={current}
        onChange={(e, inputProps) =>
          setCurrent(Math.min(Math.max(parseInt(inputProps.value), 0), 32))
        }
      />
      <Button
        onClick={() => {
          confirmSendCommand(
            'HUB_OVERRIDE_CURRENT_AXON',
            axonId,
            current,
            'Are you sure you want to override booked current for AXON(s) to ' +
              current +
              ' amps?',
            false
          );
        }}
        color="olive"
      >
        Override booked current
      </Button>
      <Button
        onClick={() => {
          confirmSendCommand(
            'HUB_OVERRIDE_CURRENT_AXON',
            axonId,
            100, // Any value over 32A is invalid and cancels the override
            'Are you sure you want to cancel the booked current override?',
            false
          );
        }}
        color="olive"
      >
        Cancel override
      </Button>
      <br />
      <br />
      <Dropdown
        selection
        search
        options={axonIdOptions}
        onChange={(e, inputProps) => {
          setAxonId(inputProps.value);
        }}
        value={axonId}
      />
      <Input
        type="number"
        label={'Command Byte (0-255)'}
        value={cmd}
        onChange={(e, inputProps) =>
          setCmd(Math.min(Math.max(parseInt(inputProps.value), 0), 255))
        }
      />
      <Button
        onClick={() => {
          confirmSendCommand(
            'HUB_SEND_AXON_COMMAND',
            axonId,
            cmd,
            'Are you sure you want to send command byte ' +
              cmd +
              ' to AXON(s)?',
            false
          );
        }}
        color="olive"
      >
        Send AXON command
      </Button>
      <br />
      <br />
      <Dropdown
        selection
        search
        options={satecConfigOptions}
        onChange={(e, inputProps) => {
          setSatecConfig(inputProps.value);
        }}
        value={satecConfig}
      />
      <Button
        onClick={() => {
          confirmSendCommand(
            'HUB_CONFIGURE_SATEC_METER',
            satecConfig,
            '',
            'Are you sure you want to configure the SATEC meter?',
            false
          );
        }}
        color="olive"
      >
        Configure SATEC meter
      </Button>
      <br />
      <br />
      <Input
        type="number"
        label={'Minutes'}
        value={satecPauseMinutes}
        onChange={(e, inputProps) =>
          setSatecPauseMinutes(Math.max(parseInt(inputProps.value), 0))
        }
      />
      <Button
        onClick={() => {
          confirmSendCommand(
            'HUB_PAUSE_SATEC_METER_READINGS',
            satecPauseMinutes,
            '',
            `Are you sure you want to pause the SATEC meter readings for ${satecPauseMinutes} minutes?`,
            false
          );
        }}
        color="olive"
      >
        Pause SATEC meter readings
      </Button>
      <br />
      <Header as="h2">Modbus</Header>
      <Segment style={{ width: '430px' }}>
        <Input
          type="number"
          label={'Pause length (minutes)'}
          value={pauseModbusServerMinutes}
          onChange={(e, inputProps) =>
            setPauseModbusServerMinutes(
              Math.min(Math.max(parseInt(inputProps.value), 0), 15)
            )
          }
          style={{ width: '100px' }}
        />
        <br />
        <br />
        <Button
          onClick={() => {
            confirmSendCommand(
              'HUB_PAUSE_MODBUS_SERVER',
              pauseModbusServerMinutes,
              '',
              '',
              'Are you sure you want to pause the Modbus server?',
              false
            );
          }}
          color="olive"
          disabled={phalanx?.status === 'Capped'}
        >
          Pause Modbus Server
        </Button>
      </Segment>
      <Header as="h2">General Message</Header>
      <Input
        type="number"
        label={'Length'}
        value={messageLength}
        onChange={(e, inputProps) =>
          setMessageLength(
            Math.min(Math.max(parseInt(inputProps.value), 0), 255)
          )
        }
      />
      <Input
        label={'Hex bytes'}
        style={{ width: '450px' }}
        value={messageByteString}
        onChange={(e, inputProps) => setMessageByteString(inputProps.value)}
      />
      <br />
      <br />
      <Button
        onClick={() => {
          const messageParamString = messageByteString
            .match(/.{2}/g)
            .reverse()
            .join('_');

          confirmSendCommand(
            'SEND_MESSAGE',
            messageLength,
            messageParamString,
            'Are you sure you want to send this message?',
            false
          );
        }}
        color="olive"
        disabled={!messageByteStringValid}
      >
        Send message
      </Button>
      <br />
      <Modal open={isModalOpen} size="small" style={{ padding: '20px' }}>
        <Modal.Content>
          {confirmation}
          <br />
          <br />
          <br />
          <Button basic color="olive" onClick={sendCommand}>
            Yes
          </Button>
          <Button
            basic
            onClick={event => {
              setIsModalOpen(false);
            }}
          >
            No
          </Button>
        </Modal.Content>
      </Modal>
    </Segment>
  );
};
